import { Divider, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';

import { useChartColorDefinitions } from '@/components/charts/constants';
import { ColorBox } from '@/components/display/ColorBox/ColorBox';
import { DiagonalSplitColorBox } from '@/components/display/ColorBox/DiagonalSplitColorBox';
import { OversizedMetricItem } from '@/components/display/OversizedMetricItem/OversizedMetricItem';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { formatCurrency } from '@/utils/formatting/currency';

import { useEstateValueMetricsColors } from './hooks/useEstateValueMetricsColors';
import { useHouseholdEstateValueMetrics } from './hooks/useHouseholdEstateValueMetrics';
import {
  COMPACT_CURRENCY_OPTIONS,
  EstateValuePieChart,
  formatCompactCurrency,
  formatLiquidityLabel,
  HouseholdAssetsSummary,
} from './HouseholdEstateValueMetrics.utils';
import { HouseholdEstateValueMetricsCard } from './HouseholdEstateValueMetricsCard';
import { NetWorthInsuranceTooltip } from './NetWorthInsuranceTooltip';

export interface HouseholdEstateValueMetrics {
  totalEstateValue: Decimal;
  inEstateValue: Decimal;
  outOfEstateValue: Decimal;
  outOfEstateCharityValue: Decimal;
  outOfEstateFamilyValue: Decimal;
}

export interface HouseholdEstateValueMetricsProps {
  metrics?: HouseholdEstateValueMetrics | null;
  assetsSummary?: HouseholdAssetsSummary | null;
}

export function HouseholdEstateValueMetrics({
  metrics,
  assetsSummary,
}: HouseholdEstateValueMetricsProps) {
  const colors = useChartColorDefinitions();
  const { titleColor, valueColor, subtitleColor, dividerColor } =
    useEstateValueMetricsColors();

  const { pieChartSeriesData, onePercentOfSum } =
    useHouseholdEstateValueMetrics({
      metrics,
    });

  return (
    <HouseholdEstateValueMetricsCard>
      <Stack direction="row" spacing={3} alignItems="baseline">
        {/* Total net worth */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          alignSelf="center"
        >
          <EstateValuePieChart
            pieChartSeriesData={pieChartSeriesData}
            onePercentOfSum={onePercentOfSum}
          />
          <Stack spacing={0.25} minWidth={204}>
            <OversizedMetricItem
              title={
                <Typography variant="body-s" sx={{ color: titleColor }}>
                  Total net worth
                </Typography>
              }
              tooltip={<NetWorthInsuranceTooltip />}
              valueVariant="title-l"
              valueColor={valueColor}
              value={
                metrics
                  ? formatCurrency(
                      metrics.totalEstateValue,
                      COMPACT_CURRENCY_OPTIONS
                    )
                  : EMPTY_CONTENT_HYPHEN
              }
            />
            {assetsSummary && (
              <Typography variant="body-xs" sx={{ color: valueColor }}>
                {formatLiquidityLabel(
                  assetsSummary.liquidValue,
                  assetsSummary.liquidPercentage
                )}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            backgroundColor: dividerColor,
            alignSelf: 'stretch',
          }}
        />

        {/* In estate */}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Stack pt="2px">
            <ColorBox color={colors.PRIMARY.backgroundColor} width={16} />
          </Stack>
          <Stack spacing={0.25}>
            <OversizedMetricItem
              title={
                <Typography variant="body-s" sx={{ color: titleColor }}>
                  In estate
                </Typography>
              }
              valueVariant="title-l"
              valueColor={valueColor}
              value={
                metrics
                  ? formatCurrency(
                      metrics.inEstateValue,
                      COMPACT_CURRENCY_OPTIONS
                    )
                  : EMPTY_CONTENT_HYPHEN
              }
            />
            {assetsSummary && (
              <Typography variant="body-xs" sx={{ color: valueColor }}>
                {formatLiquidityLabel(
                  assetsSummary.inEstateLiquidValue,
                  assetsSummary.inEstateLiquidPercentage
                )}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            backgroundColor: dividerColor,
            alignSelf: 'stretch',
          }}
        />

        {/* Out of estate */}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Stack pt="2px">
            <DiagonalSplitColorBox
              topLeftColor={colors.SECONDARY.backgroundColor}
              bottomRightColor={colors.TERTIARY.backgroundColor}
              width={16}
            />
          </Stack>
          <Stack spacing={0.25}>
            <OversizedMetricItem
              title={
                <Typography variant="body-s" sx={{ color: titleColor }}>
                  Out of estate
                </Typography>
              }
              valueVariant="title-l"
              valueColor={valueColor}
              value={
                metrics
                  ? formatCurrency(
                      metrics.outOfEstateValue,
                      COMPACT_CURRENCY_OPTIONS
                    )
                  : EMPTY_CONTENT_HYPHEN
              }
            />
            {assetsSummary && (
              <Typography variant="body-xs" sx={{ color: valueColor }}>
                {formatLiquidityLabel(
                  assetsSummary.outOfEstateLiquidValue,
                  assetsSummary.outOfEstateLiquidPercentage
                )}
              </Typography>
            )}
          </Stack>
        </Stack>

        {/* Family / Charitable giving */}
        <Stack direction="row" spacing={2.5} alignItems="flex-end" pb="4px">
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="flex-start"
            pt="3px"
          >
            <Stack pt="2px">
              <ColorBox color={colors.SECONDARY.backgroundColor} width={12} />
            </Stack>
            <Stack spacing={0.75}>
              <Typography variant="body-xs" sx={{ color: subtitleColor }}>
                Family giving
              </Typography>
              <Typography
                variant="title-s"
                sx={{
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  color: valueColor,
                }}
              >
                {formatCompactCurrency(metrics?.outOfEstateFamilyValue)}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="flex-start"
            pt="3px"
          >
            <Stack pt="2px">
              <ColorBox color={colors.TERTIARY.backgroundColor} width={12} />
            </Stack>
            <Stack spacing={0.75}>
              <Typography variant="body-xs" sx={{ color: subtitleColor }}>
                Charitable giving
              </Typography>
              <Typography
                variant="title-s"
                sx={{
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  color: valueColor,
                }}
              >
                {formatCompactCurrency(metrics?.outOfEstateCharityValue)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </HouseholdEstateValueMetricsCard>
  );
}
